import '@fontsource/roboto/500.css';
import AddIcon from '@mui/icons-material/Add';
import { Avatar, Box, Button, Dialog, Fade, Grid, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableRow, Typography, useMediaQuery, useTheme } from '@mui/material';
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import { lighten } from 'polished';
import React, { Fragment, forwardRef, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useLocation, useNavigate } from 'react-router-dom';
import DesktopBackgroundDark from '../../images/desktop-background-dark.svg';
import DesktopBackgroundDefault from '../../images/desktop-background-default.svg';
import DesktopBackgroundLight from '../../images/desktop-background-light.svg';
import logoDark from '../../images/logo-dark.png';
import logoDefault from '../../images/logo-default.png';
import logoLight from '../../images/logo-light.png';
import { MessageContext } from '../../utils/MessageContext';
import { UserContext } from '../../utils/UserContext';
import { useAxios } from '../../utils/useAxios';
import { useHasFocus } from '../../utils/useHasFocus';
import { useInterval } from '../../utils/useInterval';
import DashboardDrawer from '../drawer/DasboardDrawer';
import DashboardListItem from './DashboardListItem';
import MobilBoxDialog from './MobilBoxDialog';
import QuickStart from './Quickstart';

export default function Dashboard({ load, displayContent }) {
    const fallbackPaperWidth = window.innerWidth * .56;

    const location = useLocation();
    const navigate = useNavigate();

    const query = new URLSearchParams(location.search);
    const fromlogin = JSON.parse(query.get('fromlogin'));

    const theme = useTheme();
    const belowsm = useMediaQuery(theme.breakpoints.down("sm"));

    const { user, setUser } = useContext(UserContext);
    const { messages, setMessages } = useContext(MessageContext);

    const axiosInstance = useAxios();
    const focus = useHasFocus();

    const [hidden, setHidden] = useState(true);
    const [fadeContent, setFadeContent] = useState(false);
    const [height, setHeight] = useState(document.body.clientHeight < 700 ? document.body.clientHeight * .03 : document.body.clientHeight * .1);
    const [width, setWidth] = useState(document.getElementById("entries-container")?.clientWidth || "90%");
    const [paperWidth, setPaperWidth] = useState(fallbackPaperWidth);


    const drawerRef = useRef();
    const attemptMessages = useRef(true);
    const paperRef = useRef();

    const { t } = useTranslation();

    const auth = useAuth();

    const handleWidth = () => {
        setWidth(document.getElementById("entries-container")?.clientWidth || "90%");
    };
    const handlePaperWidth = (event) => {
        const newWidth = paperRef.current.offsetWidth;
        // console.log(`running resize listener\n${newWidth}`)
        setPaperWidth(newWidth || fallbackPaperWidth);
    };
    const handleHeight = () => {
        if (document.body.clientHeight < 700) {
            setHeight(document.body.clientHeight * .05);
        } else {
            setHeight(document.body.clientHeight * .1);
        }
    };

    useEffect(() => {
        sessionStorage.removeItem("redirect");
        document.title = t("vmm-dashboard")
        document.body.style.backgroundColor = (belowsm || hidden || fromlogin) ? theme.palette.background.default : theme.palette.background.secondary;
        document.body.style.backgroundImage = (belowsm || hidden || fromlogin) ? "none" : `url(${theme.palette.mode === "light" ? theme.palette.primary.main === "#0075FF" ? DesktopBackgroundDefault : DesktopBackgroundLight : DesktopBackgroundDark})`;
        document.body.style.backgroundSize = "cover";
        document.body.style.backgroundPosition = "-260px 380px";
        document.body.style.backgroundRepeat = "no-repeat";
        if (paperRef.current && width !== paperRef.current.offsetWidth) {
            const newWidth = paperRef.current.offsetWidth;
            // console.log(`running this if\n${newWidth}`)
            setPaperWidth(newWidth ? newWidth : fallbackPaperWidth);
        }
        if (user.userID === undefined) {
            fetchUser();
        }
        if (!localStorage.getItem("url")) {
            const token = jwtDecode(auth.user?.id_token);
            fetchCustomer(token?.email);
        }
        if (messages?.length === 0 && attemptMessages.current && user.email) {
            fetchMessages();
        }
        if (Boolean(sessionStorage.getItem("displayContent")) === true) {
            sessionStorage.removeItem("displayContent");
            displayContent.current = true;
            setFadeContent(true);
            setHidden(false);
        }
        if (displayContent.current === false) {
            let timeout = setTimeout(() => { setFadeContent(true); setHidden(false); clearSearchParams(); }, focus ? 2600 : 0);
            return () => {
                clearTimeout(timeout);
            };
        } else {
            setFadeContent(true);
            setHidden(false);
        }
        window.addEventListener("resize", handleWidth);
        //fix this rather by making the content of the listitem fit
        window.addEventListener("resize", handlePaperWidth);
        window.addEventListener("resize", handleHeight);

        return () => {
            window.removeEventListener("resize", handleWidth);
            window.removeEventListener("resize", handlePaperWidth);
            window.removeEventListener("resize", handleHeight);
        };
        //eslint-disable-next-line
    }, [user, belowsm, hidden, fromlogin, messages, theme.palette.mode, height, width, user.email])

    //this function fetches entries every 3 seconds 
    useInterval(() => {
        // console.log("fetching entries for user: ", user.email);
        if (focus && user.email !== "account-not-active" && JSON.parse(process.env.REACT_APP_automatic_refresh)) {
            getEntries();
        }
    }, 3000);

    const getEntries = async () => {
        await axiosInstance.current.get("/entry").then(res => {
            if (JSON.stringify(res.data) !== JSON.stringify(user.calendarEntries)) {
                setUser({
                    ...user,
                    calendarEntries: res.data
                });
            }
        }).catch(err => {
            if (err.response) {
                console.error("entries err: ", err.response)
            }
        })
    }

    const fetchUser = async () => {
        await axiosInstance.current.get("/user").then(res => {
            if (res.status === 202) {
                setUser({
                    "userID": "account-not-active",
                    "mobilboxPassword": "****",
                    "surName": t("inactive"),
                    "familyName": t("user"),
                    ...user
                })
            } else {
                setUser({
                    ...res.data,
                    "vpnNumber": res.data.vpnNumber || "",
                    "email": res.data.email || "",
                    // calendarEntries: [
                    //     {
                    //         "id": "2152406",
                    //         "announcementName": "Office Vertretung mit Rückkehrdatum",
                    //         "optionalDisplayName": "Sommerurlaub",
                    //         "companyMessageUid": 343,
                    //         "startDate": "2024-05-17T22:00:00Z",
                    //         "endDate": "2024-06-03T22:00:00Z",
                    //         "notificationType": 0,
                    //         "repeatType": 0
                    //     }
                    // ]
                });
            }
        }).catch(err => {
            if (err.response) {
                console.log("user err: ", err.response)
            }
        })
    };

    const fetchCustomer = async (email) => {
        await axios.get(process.env.REACT_APP_apiUri + `/customer?email=${encodeURIComponent(email)}`).then(res => {
            if (res.data?.length > 0) {
                localStorage.setItem("client", res.data[0].customerID === "mbm-connector-generic" ? "connector" : res.data[0].customerID);
                localStorage.setItem("url", res.data[0].url);
                if (res.data[0].logo) {
                    localStorage.setItem("logo", res.data[0].logo);
                }
                if (res.data[0].authority) {
                    localStorage.setItem("authority", res.data[0].authority);
                }
                if (res.data[0].externalClientId) {
                    localStorage.setItem("externalClientId", res.data[0].externalClientId);
                }
                localStorage.setItem("primary", res.data[0].color);
                window.location.reload();
            }
        }).catch(err => {
            console.error(err.response);
        });
    };

    const fetchMessages = async () => {
        // console.log("fetching messages from Dashboard.js");
        // console.log("user: ", user);
        await axiosInstance.current.get("/messages/?email=" + encodeURIComponent(user?.email)).then(res => {
            if (res.data?.length > 0) {
                setMessages(res.data);
                attemptMessages.current = false;
            } else {
                attemptMessages.current = false;
            }
        });
    };

    //pass entry values to drawer via ref
    const passToDrawer = (e) => {
        drawerRef.current.handleEntry(e);
    };

    const passToDrawerAsQuickStart = (qs) => {
        drawerRef.current.handleQuickStart(qs);
    }

    const clearSearchParams = () => {
        navigate({
            pathname: location.pathname,
        }, { replace: true });
    }

    const DesktopContent = forwardRef((props, ref) => {
        return (
            <div ref={ref} {...props}>
                <Grid container justifyContent="center" sx={{ visibility: hidden || fromlogin ? "hidden" : "visible" }}>
                    <Grid item xs={11} sm={11} md={9} lg={7}>
                        <Grid container sx={{ mt: 5, mb: 1 }}>
                            <Grid item sm={7} md={6}>
                                <Typography noWrap sx={{ color: "text.primary", fontWeight: 700, lineHeight: "normal", fontSize: "40px", letterSpacing: "-1px", ml: 2 }}>{t("voicemails")}</Typography>
                            </Grid>
                            <Grid item sm={5} md={6}>
                                <Typography noWrap sx={{ color: "text.primary", fontWeight: 700, lineHeight: "normal", fontSize: "20px", letterSpacing: "-0.5px", mt: 2.7 }}>{t("saved-voicemails")}</Typography>
                            </Grid>
                        </Grid>
                        <Paper
                            ref={paperRef}
                            sx={{
                                borderRadius: "36px",
                                background: theme.palette.mode === "dark" ? "rgba(12, 12, 12, 0.35)" : "rgba(255, 255, 255, 0.35)",
                                backdropFilter: "blur(5px)",
                                width: "100%",
                                height: 446,
                                overflow: "hidden"
                            }}
                        >
                            <Grid container sx={{ mt: 1, mb: 1 }}>
                                <Grid item xs={6} sx={{ pl: 2, pr: 3 }}>
                                    <Grid container direction="column">
                                        <QuickStart drawer={passToDrawerAsQuickStart} />
                                        <Button
                                            variant="contained"
                                            onClick={() => { drawerRef.current.open() }}
                                            disabled={user.userID === undefined || user.userID === "account-not-active"}
                                            startIcon={
                                                <Avatar sx={{ bgcolor: user.userID !== undefined ? "primary.contrastText" : "action.hover", height: 43, width: 43, ml: 0.15 }}>
                                                    <AddIcon sx={{ color: !user.userID || user.userID === "account-not-active" ? "action.disabled" : "primary.main", fontSize: 32 }} />
                                                </Avatar>
                                            }
                                            sx={{
                                                bgcolor: user.userID !== undefined ? "primary.main" : "action.disabled",
                                                borderRadius: 6,
                                                textTransform: "none",
                                                justifyContent: "flex-start",
                                                pl: 1,
                                                mr: 2,
                                                mt: 10,
                                                height: 52,
                                                "&:hover": { bgcolor: lighten(0.05, theme.palette.primary.main) }
                                            }}>
                                            <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                                                <Typography sx={{ color: user.userID === undefined || user.userID === "account-not-active" ? "text.primary" : "primary.contrastText", fontSize: 22, fontWeight: user.userID === undefined ? "normal" : "bold" }}>{t("new-entry")}</Typography>
                                            </Box>
                                        </Button>
                                    </Grid>
                                </Grid>
                                <Grid item xs={6}>
                                    <TableContainer sx={{ p: 2, pt: 3, pl: 0, maxHeight: 430, "::-webkit-scrollbar": { display: "none" } }}>
                                        <Table>
                                            <TableBody>
                                                {user.userID !== undefined && user?.calendarEntries?.length > 0 ?
                                                    user.calendarEntries?.map((e, i) => { return <DashboardListItem key={`ListItem-${i}`} item={e} index={i} drawer={passToDrawer} paperWidth={paperWidth} desktop /> }) : user.userID !== undefined ?
                                                        <TableRow>
                                                            <TableCell sx={{ display: "flex", justifyContent: "center", width: "100%", height: 219, borderBottom: 0, alignItems: "center", mt: 1 }}>
                                                                <Typography color="text.secondary">{t("no-voicemails-planned")}</Typography>
                                                            </TableCell>
                                                        </TableRow> :
                                                        <Fragment>
                                                            <TableRow key={"loading-0"}>
                                                                <TableCell sx={{ p: 0, borderBottom: 0 }}>
                                                                    <Skeleton animation="wave" variant="rectangular" height={70} sx={{ borderRadius: 3, mb: 1, mt: 2 }} />
                                                                </TableCell>
                                                            </TableRow>
                                                            <TableRow key={"loading-1"} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                                <TableCell sx={{ p: 0 }}>
                                                                    <Skeleton animation="wave" variant="rectangular" height={70} sx={{ borderRadius: 3 }} />
                                                                </TableCell>
                                                            </TableRow>
                                                        </Fragment>
                                                }
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>
            </div>
        )
    });

    const MobileCalendarEntriesContent = () => {
        return (
            <TableContainer id="entries-container" sx={{ border: 0 }}>
                <Table>
                    <TableBody>
                        {user.userID !== undefined && user?.calendarEntries?.length > 0 ?
                            user.calendarEntries?.map((e, i) => { return <DashboardListItem key={`ListItem-${i}`} item={e} index={i} drawer={passToDrawer} /> }) : user.userID !== undefined ?
                                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                                        <Box sx={{ height: height }} />
                                        <Typography color="text.secondary">{t("no-voicemails-planned")}</Typography>
                                    </TableCell>
                                </TableRow> :
                                <TableRow key={"loading"} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell sx={{ p: 0 }}>
                                        <Skeleton animation="wave" variant="rectangular" height={70} sx={{ borderRadius: 3 }} />
                                    </TableCell>
                                </TableRow>
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    const SplashScreen = ({ load, setHidden }) => {

        const [open, setOpen] = useState(true);

        useEffect(() => {
            if (load.current) {
                let duration = setTimeout(() => { setOpen(false); load.current = false; setHidden(false); clearSearchParams(); }, focus ? 2600 : 0);
                return () => {
                    clearTimeout(duration);
                };
            } else {
                // setAnimate(false);
                setOpen(false);
            }
            //eslint-disable-next-line
        }, [])

        return (
            <Dialog
                open={open}
                onClose={() => { setOpen(false); }}
                fullScreen
                hideBackdrop
                transitionDuration={{
                    enter: focus ? 2000 : 0,
                    exit: focus ? 600 : 0
                }}
                sx={{
                    "& .MuiDialog-paper": {
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: belowsm ? "background.default" : "background.secondary",
                        backgroundImage: belowsm ? "none" : `url(${theme.palette.mode === "light" ? theme.palette.primary.main === "#0075FF" ? DesktopBackgroundDefault : DesktopBackgroundLight : DesktopBackgroundDark})`,
                        backgroundSize: "cover",
                        backgroundPosition: "-260px 380px",
                        backgroundRepeat: "no-repeat"
                    }
                }}
            >
                <Box key="nav" sx={{ height: 90, width: "100%", bgcolor: "background.default", position: "absolute", top: 0 }} />
                <Box sx={{ pl: 7.5, pt: 20 }}>
                    {belowsm ?
                        <img src={theme.palette.primary.main === "#0075FF" ? logoDefault : theme.palette.mode === "dark" ? logoDark : logoLight} alt="vmm-logo" height="250vh" /> :
                        null
                    }
                </Box>
                <Grid container justifyContent="center" sx={{ pt: 20 }}>
                    <Typography sx={{ color: "text.primary", lineHeight: "34px", fontSize: 30, letterSpacing: "-1px", position: belowsm ? "relative" : "absolute", bottom: belowsm ? "auto" : 20, right: belowsm ? "auto" : 20 }}>{t("powered-by")}</Typography>
                </Grid>
            </Dialog>
        )
    };

    return (
        <Fragment>
            <DashboardDrawer ref={drawerRef} />
            {belowsm ?
                <Box sx={{ bgcolor: "transparent", visibility: hidden || fromlogin ? "hidden" : "visible" }}>
                    <Typography sx={{ color: "text.primary", fontWeight: 700, lineHeight: "34px", fontSize: 30, mt: -3, ml: 2, position: "relative", zIndex: theme.zIndex.appBar + 101 }}>{t("voicemails")}</Typography>
                    <Box sx={{ mt: -.5, ml: 2, mr: 1 }}>
                        <QuickStart drawer={passToDrawerAsQuickStart} />
                    </Box>
                    <Typography sx={{ color: "text.primary", fontWeight: "bold", fontSize: 20, ml: belowsm ? 2 : 0, mt: belowsm ? 2 : -1 }}>{t("saved-voicemails")}</Typography>
                </Box>
                :
                null
            }
            {
                belowsm ?
                    <Box sx={{ mx: 2, mt: 2.5, mb: user.calendarEntries?.length > 0 ? 9 : 0, visibility: hidden || fromlogin ? "hidden" : "visible" }}>
                        <MobileCalendarEntriesContent />
                    </Box>
                    :
                    <Fade
                        in={fadeContent}
                        timeout={{
                            enter: displayContent.current || !focus ? 0 : 1000,
                            exit: displayContent.current || !focus ? 0 : 600
                        }}
                        addEndListener={() => { displayContent.current = true; }}
                    >
                        <DesktopContent />
                    </Fade>
            }
            <Box sx={{ display: belowsm ? "inline" : "none", visibility: hidden || fromlogin ? "hidden" : "visible", mt: 2, ml: 2, mr: 3 }}>
                <Button
                    variant="contained"
                    onClick={() => {
                        // Check if there is at least one message with a non-empty announcementSubtitle
                        const hasAnnouncementSubtitle = Array.isArray(messages) && messages.some(m => typeof m.announcementSubtitle === "string" && m.announcementSubtitle.trim() !== "");
                        console.log('hasAnnouncementSubtitle:', hasAnnouncementSubtitle);
                        
                        // If such a message exists, open the drawer directly; otherwise, open the wizard i.e. MobileSelection.js
                        if (hasAnnouncementSubtitle) {
                            drawerRef.current.open();
                        } else {
                            // TODO: Testen ob via wizard time richtig gesetzt wird (in 15 min Schritten)
                            navigate("/select?type=announcement");
                        }
                    }}
                    disabled={user.userID === undefined || user.userID === "account-not-active"}
                    startIcon={<Avatar sx={{ bgcolor: user.userID !== undefined ? "primary.contrastText" : "action.hover", height: 43, width: 43, ml: .15 }}>
                        <AddIcon sx={{ color: user.userID === undefined || user.userID === "account-not-active" ? "info.main" : "primary.main", fontSize: 32 }} />
                    </Avatar>}
                    sx={{
                        bgcolor: user.userID !== undefined ? "primary.main" : "action.disabled",
                        position: "fixed",
                        bottom: (theme) => theme.spacing(3),
                        borderRadius: 6,
                        textTransform: "none",
                        justifyContent: "flex-start",
                        width: width,
                        pl: 1,
                        height: 52,
                        "&:hover": { bgcolor: lighten(0.05, theme.palette.primary.main) }
                    }}>
                    <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                        <Typography sx={{ color: user.userID === undefined ? "text.primary" : "primary.contrastText", fontSize: 22, fontWeight: user.userID === undefined ? "normal" : "bold" }}>{t("new-entry")}</Typography>
                    </Box>
                </Button>
            </Box>
            <MobilBoxDialog />
            <SplashScreen load={load} setHidden={setHidden} />
        </Fragment>
    )
}
